import React, { useState, useCallback, useMemo } from "react";
import { Modal, Flex, List, Button, Popover, message } from "antd";
import { CloseCircleOutlined } from "@ant-design/icons";
import EmojiPicker from "emoji-picker-react";
import EmojiIcon from "../../../assets/icons/Emoji";
import ApiService from "../../../API/apiService";
import { API_ENDPOINTS } from "../../../API/endpoints";
import { handleApiError } from "../../../utils/CommonFunctions";

const PREDEFINED_STATUS_OPTIONS = [
  {
    title: "In a meeting",
    icon: "https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/1f4c5.png",
  },
  {
    title: "Out for lunch",
    icon: "https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/1f354.png",
  },
  {
    title: "Out sick",
    icon: "https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/1f927.png",
  },
  {
    title: "Working from home",
    icon: "https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/1f3e1.png",
  },
  {
    title: "On a vacation",
    icon: "https://cdn.jsdelivr.net/npm/emoji-datasource-apple/img/apple/64/1f334.png",
  },
];

const CustomUserStatusModal = ({
  userSelfData,
  onUserDetailsUpdate,
  onModalUpdate,
}) => {
  const [customStatusValue, setCustomStatusValue] = useState("");
  const [emojiData, setEmojiData] = useState({ emoji: "" });
  const [isOpenEmoji, setIsOpenEmoji] = useState(false);

  const handleEmojiClicked = useCallback((emojiChangeData) => {
    setEmojiData(emojiChangeData);
    setIsOpenEmoji(false);
  }, []);

  const clearEmojiIcon = useCallback(() => {
    setEmojiData({ emoji: "" });
    setCustomStatusValue("");
  }, []);

  const handleCloseModal = useCallback(() => {
    onModalUpdate({ activeModal: "" });
  }, [onModalUpdate]);

  const handleCustomStatus = useCallback(
    async (clearStatus = false) => {
      if (!userSelfData?._id) return;

      const reqBody = {
        custom_status: clearStatus ? "" : customStatusValue,
        custom_status_emoji: clearStatus ? "" : emojiData.imageUrl || "",
        user_id: userSelfData._id,
        is_custom_status: true,
      };

      try {
        const response = await ApiService.post(
          API_ENDPOINTS.changestatus,
          reqBody
        );

        if (response?.data && response?.statusCode === 200) {
          if (!clearStatus) {
            handleCloseModal();
          }
          onUserDetailsUpdate(response.data);
          setCustomStatusValue("");
          setEmojiData({ emoji: "", imageUrl: "" });
        } else {
          handleCloseModal();
          setCustomStatusValue("");
          message.error(response?.data?.message || "Error setting status");
          setEmojiData({ emoji: "", imageUrl: "" });
        }
      } catch (error) {
        handleApiError(error);
      }
    },
    [
      customStatusValue,
      emojiData,
      userSelfData,
      onUserDetailsUpdate,
      handleCloseModal,
    ]
  );

  const handleStatusValueChange = useCallback((e) => {
    if (e.target.value.length <= 50) {
      setCustomStatusValue(e.target.value);
    }
  }, []);

  const handlePredefinedStatusClick = useCallback((item) => {
    setCustomStatusValue(item.title);
    setEmojiData({ emoji: item.title, imageUrl: item.icon });
  }, []);

  const handleEmojiOpenChange = useCallback((open) => {
    setIsOpenEmoji(open);
  }, []);

  const RenderEmojiContainer = useMemo(
    () => <EmojiPicker onEmojiClick={handleEmojiClicked} />,
    [handleEmojiClicked]
  );

  const isButtonDisabled = customStatusValue.trim() === "";
  const hasEmojiOrStatus = Boolean(emojiData.emoji) || customStatusValue !== "";

  return (
    <Modal
      title="Set a status"
      open={true}
      onCancel={handleCloseModal}
      footer={
        <Button
          type="text"
          onClick={() => handleCustomStatus()}
          className="primary-btn"
          style={{ padding: "10px" }}
          disabled={isButtonDisabled}
        >
          Set Status
        </Button>
      }
      className="ec-modal set-custom-status-modal"
      width={600}
      style={{ top: 90 }}
    >
      <Flex gap="small" className="ant-modal-subheader">
        <div className="search-container">
          <Popover
            trigger="click"
            arrow={false}
            placement="bottom"
            content={RenderEmojiContainer}
            open={isOpenEmoji}
            onOpenChange={handleEmojiOpenChange}
          >
            {emojiData.emoji ? (
              <img
                className="search-icon"
                width={20}
                height={20}
                src={emojiData.imageUrl}
                alt="emoji"
              />
            ) : (
              <i className="search-icon">
                <EmojiIcon />
              </i>
            )}
          </Popover>
          <input
            type="text"
            className="custom-search"
            placeholder="Set a status"
            value={customStatusValue}
            onChange={handleStatusValueChange}
          />
          {hasEmojiOrStatus && (
            <Button className="search-clear" onClick={clearEmojiIcon}>
              <CloseCircleOutlined />
            </Button>
          )}
        </div>
      </Flex>

      <Flex vertical className="suggestion-list-container">
        {customStatusValue === "" && (
          <List
            className="suggestion-list"
            itemLayout="horizontal"
            dataSource={PREDEFINED_STATUS_OPTIONS}
            renderItem={(item) => (
              <List.Item>
                <List.Item.Meta
                  onClick={() => handlePredefinedStatusClick(item)}
                  title={
                    <Flex gap="small">
                      <img
                        className="emoji"
                        width={20}
                        height={20}
                        src={item.icon}
                        alt={item.title}
                      />
                      <span className="title">{item.title}</span>
                    </Flex>
                  }
                />
              </List.Item>
            )}
          />
        )}
      </Flex>
    </Modal>
  );
};

export default CustomUserStatusModal;
